import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { twofaService } from '../services/twofaService';
import { webauthnService } from '../services/webauthnService';
import { Shield, ShieldCheck, AlertCircle, CheckCircle, Copy, Key, Smartphone, History, Usb, Trash2, Edit2, Plus, Loader2, Monitor, Laptop, XCircle } from 'lucide-react';
import api from '../services/api';
import SpaceBackground from '../components/layout/SpaceBackground';

const Security = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // 2FA state
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [totpCode, setTotpCode] = useState('');

  // Security Keys (WebAuthn) state
  const [securityKeys, setSecurityKeys] = useState([]);
  const [webauthnSupported, setWebauthnSupported] = useState(false);
  const [showAddKey, setShowAddKey] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [addingKey, setAddingKey] = useState(false);
  const [renamingKeyId, setRenamingKeyId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  // Sessions state
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  // Trusted devices state
  const [trustedDevices, setTrustedDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);

  useEffect(() => {
    load2FAStatus();
    loadSecurityKeys();
    loadSessions();
    loadTrustedDevices();
    setWebauthnSupported(webauthnService.isSupported());
  }, []);

  const load2FAStatus = async () => {
    try {
      const status = await twofaService.get2FAStatus();
      setIs2FAEnabled(status.is_2fa_enabled);
    } catch (err) {
      console.error('Failed to load 2FA status:', err);
    }
  };

  const loadSecurityKeys = async () => {
    try {
      const keys = await webauthnService.getKeys();
      setSecurityKeys(keys);
    } catch (err) {
      console.error('Failed to load security keys:', err);
    }
  };

  const loadSessions = async () => {
    setLoadingSessions(true);
    try {
      const res = await api.get('/auth/sessions');
      setSessions(res.data);
    } catch (err) {
      console.error('Failed to load sessions:', err);
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadTrustedDevices = async () => {
    setLoadingDevices(true);
    try {
      const res = await api.get('/auth/trusted-devices');
      setTrustedDevices(res.data);
    } catch (err) {
      console.error('Failed to load trusted devices:', err);
    } finally {
      setLoadingDevices(false);
    }
  };

  const revokeSession = async (sessionId) => {
    setError('');
    setSuccess('');
    try {
      await api.delete(`/auth/sessions/${sessionId}`);
      setSuccess('Session revoked.');
      loadSessions();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to revoke session.');
    }
  };

  const revokeAllOtherSessions = async () => {
    setError('');
    setSuccess('');
    try {
      const res = await api.delete('/auth/sessions');
      setSuccess(`Revoked ${res.data.revoked_count} other session(s).`);
      loadSessions();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to revoke sessions.');
    }
  };

  const revokeTrustedDevice = async (deviceId) => {
    setError('');
    setSuccess('');
    try {
      await api.delete(`/auth/trusted-devices/${deviceId}`);
      setSuccess('Trusted device removed.');
      loadTrustedDevices();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove device.');
    }
  };

  const revokeAllTrustedDevices = async () => {
    setError('');
    setSuccess('');
    try {
      const res = await api.delete('/auth/trusted-devices');
      setSuccess(`Removed ${res.data.revoked_count} trusted device(s).`);
      loadTrustedDevices();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove devices.');
    }
  };

  const formatTimeAgo = (isoString) => {
    if (!isoString) return 'Never';
    const diff = Date.now() - new Date(isoString).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(isoString).toLocaleDateString();
  };

  const handleAddSecurityKey = async () => {
    if (!newKeyName.trim()) {
      setError('Please enter a name for your security key');
      return;
    }

    setAddingKey(true);
    setError('');
    setSuccess('');

    try {
      await webauthnService.registerKey(newKeyName.trim());
      setSuccess('Security key registered successfully!');
      setNewKeyName('');
      setShowAddKey(false);
      loadSecurityKeys();
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Security key registration was cancelled or timed out.');
      } else if (err.name === 'InvalidStateError') {
        setError('This security key is already registered.');
      } else {
        setError(err.message || 'Failed to register security key.');
      }
    } finally {
      setAddingKey(false);
    }
  };

  const handleDeleteSecurityKey = async (keyId, keyName) => {
    if (!window.confirm(`Are you sure you want to delete the security key "${keyName}"?`)) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      await webauthnService.deleteKey(keyId);
      setSuccess('Security key deleted successfully.');
      loadSecurityKeys();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete security key.');
    }
  };

  const handleRenameSecurityKey = async (keyId) => {
    if (!renameValue.trim()) {
      setError('Please enter a name');
      return;
    }

    setError('');
    setSuccess('');

    try {
      await webauthnService.renameKey(keyId, renameValue.trim());
      setSuccess('Security key renamed successfully.');
      setRenamingKeyId(null);
      setRenameValue('');
      loadSecurityKeys();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to rename security key.');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to change password');
      }
      
      setSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordChange(false);
    } catch (err) {
      setError(err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await twofaService.enable2FA();
      setQrCode(data.qr_code_url);
      setSecret(data.secret);
      setShowSetup(true);
    } catch (err) {
      setError('Failed to enable 2FA. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const data = await twofaService.confirm2FA(totpCode);
      setBackupCodes(data.backup_codes);
      setIs2FAEnabled(true);
      setSuccess('2FA enabled successfully! Please save your backup codes.');
      setTotpCode('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async (e) => {
    e.preventDefault();
    if (!window.confirm('Are you sure you want to disable 2FA? This will make your account less secure.')) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      await twofaService.disable2FA(totpCode);
      setIs2FAEnabled(false);
      setShowSetup(false);
      setQrCode('');
      setSecret('');
      setBackupCodes([]);
      setTotpCode('');
      setSuccess('2FA has been disabled.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to disable 2FA. Invalid code.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard!');
    setTimeout(() => setSuccess(''), 2000);
  };

  const downloadBackupCodes = () => {
    const text = `Virgo 2FA Backup Codes\nUsername: ${user.username}\n\n${backupCodes.join('\n')}`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'virgo-backup-codes.txt';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-4xl mx-auto relative">
      <SpaceBackground />
      <div className="relative z-10">
      <h1 className="text-3xl font-bold text-virgo-teal-100 mb-6">Security Settings</h1>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded flex items-center">
          <AlertCircle size={20} className="mr-2" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded flex items-center">
          <CheckCircle size={20} className="mr-2" />
          <span>{success}</span>
        </div>
      )}

      {/* Password Section */}
      <div className="bg-virgo-navy-700 rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Key className="text-virgo-teal-300 mr-3" size={24} />
            <div>
              <h2 className="text-xl font-semibold text-virgo-teal-100">Password</h2>
              <p className="text-sm text-virgo-teal-300">Change your account password</p>
            </div>
          </div>
          {!showPasswordChange && (
            <button
              onClick={() => setShowPasswordChange(true)}
              className="bg-virgo-teal-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-virgo-teal-700 transition"
            >
              Change Password
            </button>
          )}
        </div>

        {showPasswordChange && (
          <form onSubmit={handlePasswordChange} className="space-y-4 mt-4 border-t pt-4">
            <div>
              <label className="block text-virgo-teal-200 text-sm font-semibold mb-2">
                Current Password
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full max-w-md px-4 py-2 border border-virgo-teal-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                required
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-virgo-teal-200 text-sm font-semibold mb-2">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full max-w-md px-4 py-2 border border-virgo-teal-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                required
                disabled={loading}
                minLength={8}
              />
            </div>
            <div>
              <label className="block text-virgo-teal-200 text-sm font-semibold mb-2">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full max-w-md px-4 py-2 border border-virgo-teal-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                required
                disabled={loading}
              />
            </div>
            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={loading}
                className="bg-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-700 transition disabled:bg-gray-400"
              >
                {loading ? 'Saving...' : 'Save Password'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowPasswordChange(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="bg-gray-300 text-virgo-teal-200 px-6 py-2 rounded-lg font-semibold hover:bg-gray-400 transition"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {/* 2FA Section */}
      <div className="bg-virgo-navy-700 rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            {is2FAEnabled ? (
              <ShieldCheck className="text-green-600 mr-3" size={24} />
            ) : (
              <Smartphone className="text-virgo-teal-500 mr-3" size={24} />
            )}
            <div>
              <h2 className="text-xl font-semibold text-virgo-teal-100">Two-Factor Authentication</h2>
              <p className="text-sm text-virgo-teal-300">
                {is2FAEnabled ? 'Your account is protected with 2FA' : 'Add an extra layer of security'}
              </p>
            </div>
          </div>
        </div>

        {!is2FAEnabled && !showSetup && (
          <div>
            <p className="text-virgo-teal-300 mb-4">
              Two-factor authentication adds an extra layer of security to your account. 
              You'll need to enter a code from your authenticator app when logging in.
            </p>
            <button
              onClick={handleEnable2FA}
              disabled={loading}
              className="bg-virgo-teal-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-virgo-teal-700 transition disabled:bg-gray-400"
            >
              {loading ? 'Setting up...' : 'Enable 2FA'}
            </button>
          </div>
        )}

        {showSetup && !backupCodes.length && (
          <div>
            <h3 className="text-lg font-semibold text-virgo-teal-100 mb-4">Setup 2FA</h3>
            <div className="space-y-4">
              <div>
                <p className="text-virgo-teal-300 mb-3">
                  1. Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                </p>
                <div className="bg-virgo-navy-700 p-4 rounded-lg border inline-block">
                  <img src={qrCode} alt="2FA QR Code" className="w-64 h-64" />
                </div>
              </div>

              <div>
                <p className="text-virgo-teal-300 mb-2">
                  2. Or manually enter this secret key:
                </p>
                <div className="flex items-center space-x-2">
                  <code className="bg-virgo-navy-800 px-3 py-2 rounded text-sm font-mono">{secret}</code>
                  <button
                    onClick={() => copyToClipboard(secret)}
                    className="text-virgo-teal-400 hover:text-virgo-teal-200"
                  >
                    <Copy size={20} />
                  </button>
                </div>
              </div>

              <div>
                <p className="text-virgo-teal-300 mb-2">
                  3. Enter the 6-digit code from your authenticator app:
                </p>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-40 px-4 py-2 border border-virgo-teal-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                    placeholder="000000"
                    maxLength={6}
                    disabled={loading}
                  />
                  <button
                    onClick={handleConfirm2FA}
                    disabled={loading || totpCode.length !== 6}
                    className="bg-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-700 transition disabled:bg-gray-400"
                  >
                    {loading ? 'Verifying...' : 'Verify & Enable'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {backupCodes.length > 0 && (
          <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-virgo-teal-100 mb-2 flex items-center">
              <AlertCircle className="text-yellow-600 mr-2" size={24} />
              Save Your Backup Codes
            </h3>
            <p className="text-virgo-teal-200 mb-4">
              Store these codes in a safe place. You can use them to access your account if you lose your authenticator device.
              Each code can only be used once.
            </p>
            <div className="bg-virgo-navy-700 rounded p-4 mb-4 font-mono text-sm">
              {backupCodes.map((code, index) => (
                <div key={index} className="py-1">{code}</div>
              ))}
            </div>
            <button
              onClick={downloadBackupCodes}
              className="bg-virgo-teal-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-virgo-teal-700 transition"
            >
              Download Backup Codes
            </button>
          </div>
        )}

        {is2FAEnabled && !showSetup && (
          <div>
            <p className="text-virgo-teal-300 mb-4">
              Two-factor authentication is currently enabled on your account.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-virgo-teal-200 text-sm font-semibold mb-2">
                  Enter your 2FA code to disable:
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-40 px-4 py-2 border border-virgo-teal-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                    placeholder="000000"
                    maxLength={6}
                    disabled={loading}
                  />
                  <button
                    onClick={handleDisable2FA}
                    disabled={loading || totpCode.length !== 6}
                    className="bg-red-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-red-700 transition disabled:bg-gray-400"
                  >
                    {loading ? 'Disabling...' : 'Disable 2FA'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Security Keys (WebAuthn/FIDO2) Section */}
      <div className="bg-virgo-navy-700 rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Usb className="text-virgo-teal-300 mr-3" size={24} />
            <div>
              <h2 className="text-xl font-semibold text-virgo-teal-100">Security Keys</h2>
              <p className="text-sm text-virgo-teal-300">
                Hardware security keys like YubiKey for passwordless login
              </p>
            </div>
          </div>
        </div>

        {!webauthnSupported ? (
          <div className="bg-yellow-900/30 border border-yellow-600/50 rounded-lg p-4">
            <p className="text-yellow-300">
              Your browser doesn't support security keys (WebAuthn). Please use a modern browser like Chrome, Firefox, or Edge.
            </p>
          </div>
        ) : (
          <>
            {/* Registered Keys List */}
            {securityKeys.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-virgo-teal-400 mb-3">Registered Security Keys</h3>
                <div className="space-y-2">
                  {securityKeys.map((key) => (
                    <div
                      key={key.id}
                      className="flex items-center justify-between bg-virgo-navy-600 rounded-lg p-4 border border-virgo-teal-700/30"
                    >
                      <div className="flex items-center">
                        <Key className="text-virgo-teal-400 mr-3" size={20} />
                        <div>
                          {renamingKeyId === key.id ? (
                            <div className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                className="px-2 py-1 bg-virgo-navy-800 border border-virgo-teal-600 rounded text-white text-sm"
                                placeholder="Key name"
                                autoFocus
                              />
                              <button
                                onClick={() => handleRenameSecurityKey(key.id)}
                                className="text-green-400 hover:text-green-300 text-sm"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => {
                                  setRenamingKeyId(null);
                                  setRenameValue('');
                                }}
                                className="text-virgo-teal-500 hover:text-virgo-teal-300 text-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <>
                              <p className="text-white font-medium">{key.device_name}</p>
                              <p className="text-xs text-virgo-teal-500">
                                Added {key.created_at ? new Date(key.created_at).toLocaleDateString() : 'Unknown'}
                                {key.last_used_at && ` • Last used ${new Date(key.last_used_at).toLocaleDateString()}`}
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                      {renamingKeyId !== key.id && (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setRenamingKeyId(key.id);
                              setRenameValue(key.device_name);
                            }}
                            className="p-2 text-virgo-teal-400 hover:text-virgo-teal-200 hover:bg-virgo-navy-500 rounded transition"
                            title="Rename"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteSecurityKey(key.id, key.device_name)}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded transition"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add New Key */}
            {!showAddKey ? (
              <button
                onClick={() => setShowAddKey(true)}
                className="flex items-center bg-virgo-teal-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-virgo-teal-700 transition"
              >
                <Plus size={20} className="mr-2" />
                Add Security Key
              </button>
            ) : (
              <div className="bg-virgo-navy-600 rounded-lg p-4 border border-virgo-teal-700/30">
                <h3 className="text-lg font-semibold text-virgo-teal-100 mb-4">Register New Security Key</h3>
                <p className="text-virgo-teal-300 mb-4">
                  Insert your security key (YubiKey, Google Titan, etc.) and give it a name to identify it.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-virgo-teal-200 text-sm font-semibold mb-2">
                      Key Name
                    </label>
                    <input
                      type="text"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      className="w-full max-w-md px-4 py-2 bg-virgo-navy-800 border border-virgo-teal-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                      placeholder="e.g., Office YubiKey, Backup Key"
                      disabled={addingKey}
                    />
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={handleAddSecurityKey}
                      disabled={addingKey || !newKeyName.trim()}
                      className="flex items-center bg-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-700 transition disabled:bg-gray-600 disabled:cursor-not-allowed"
                    >
                      {addingKey ? (
                        <>
                          <Loader2 size={20} className="mr-2 animate-spin" />
                          Touch your security key...
                        </>
                      ) : (
                        <>
                          <Usb size={20} className="mr-2" />
                          Register Key
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setShowAddKey(false);
                        setNewKeyName('');
                      }}
                      disabled={addingKey}
                      className="bg-gray-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-gray-700 transition disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {securityKeys.length === 0 && !showAddKey && (
              <p className="text-virgo-teal-400 mt-4">
                No security keys registered. Add a hardware security key for stronger account protection.
              </p>
            )}
          </>
        )}
      </div>

      {/* Active Sessions */}
      <div className="bg-virgo-navy-700 rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Monitor className="text-virgo-teal-300 mr-3" size={24} />
            <div>
              <h2 className="text-xl font-semibold text-virgo-teal-100">Active Sessions</h2>
              <p className="text-sm text-virgo-teal-300">Devices where you're currently logged in</p>
            </div>
          </div>
          {sessions.filter(s => !s.revoked_at && !s.is_current).length > 0 && (
            <button
              onClick={revokeAllOtherSessions}
              className="text-sm text-red-400 hover:text-red-300 font-medium transition"
            >
              Revoke All Others
            </button>
          )}
        </div>

        {loadingSessions ? (
          <div className="flex items-center text-virgo-teal-400 py-4">
            <Loader2 size={20} className="animate-spin mr-2" /> Loading sessions...
          </div>
        ) : sessions.filter(s => !s.revoked_at).length === 0 ? (
          <p className="text-virgo-teal-400">No active sessions found.</p>
        ) : (
          <div className="space-y-2">
            {sessions.filter(s => !s.revoked_at).map((session) => (
              <div
                key={session.id}
                className={`flex items-center justify-between rounded-lg p-4 border ${
                  session.is_current
                    ? 'bg-virgo-teal-900/20 border-virgo-teal-600/50'
                    : 'bg-virgo-navy-600 border-virgo-teal-700/30'
                }`}
              >
                <div className="flex items-center">
                  <Laptop className="text-virgo-teal-400 mr-3 flex-shrink-0" size={20} />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium">{session.device_hint || 'Unknown device'}</p>
                      {session.is_current && (
                        <span className="text-xs bg-virgo-teal-600 text-white px-2 py-0.5 rounded-full">Current</span>
                      )}
                    </div>
                    <p className="text-xs text-virgo-teal-500">
                      {session.ip_address || 'Unknown IP'}
                      {' • '}Logged in {formatTimeAgo(session.created_at)}
                      {' • '}Last seen {formatTimeAgo(session.last_seen_at)}
                    </p>
                  </div>
                </div>
                {!session.is_current && (
                  <button
                    onClick={() => revokeSession(session.id)}
                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded transition"
                    title="Revoke session"
                  >
                    <XCircle size={18} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Revoked sessions (collapsed) */}
        {sessions.filter(s => s.revoked_at).length > 0 && (
          <details className="mt-4">
            <summary className="text-sm text-virgo-teal-500 cursor-pointer hover:text-virgo-teal-400">
              {sessions.filter(s => s.revoked_at).length} revoked session(s)
            </summary>
            <div className="space-y-2 mt-2">
              {sessions.filter(s => s.revoked_at).map((session) => (
                <div
                  key={session.id}
                  className="flex items-center justify-between bg-virgo-navy-600/50 rounded-lg p-3 border border-virgo-teal-700/20 opacity-60"
                >
                  <div className="flex items-center">
                    <Laptop className="text-virgo-teal-500 mr-3 flex-shrink-0" size={18} />
                    <div>
                      <p className="text-virgo-teal-300 text-sm">{session.device_hint || 'Unknown device'}</p>
                      <p className="text-xs text-virgo-teal-600">
                        {session.ip_address} • Revoked {formatTimeAgo(session.revoked_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>

      {/* Trusted Devices */}
      <div className="bg-virgo-navy-700 rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Shield className="text-virgo-teal-300 mr-3" size={24} />
            <div>
              <h2 className="text-xl font-semibold text-virgo-teal-100">Trusted Devices</h2>
              <p className="text-sm text-virgo-teal-300">Devices that skip 2FA verification</p>
            </div>
          </div>
          {trustedDevices.length > 0 && (
            <button
              onClick={revokeAllTrustedDevices}
              className="text-sm text-red-400 hover:text-red-300 font-medium transition"
            >
              Remove All
            </button>
          )}
        </div>

        {loadingDevices ? (
          <div className="flex items-center text-virgo-teal-400 py-4">
            <Loader2 size={20} className="animate-spin mr-2" /> Loading devices...
          </div>
        ) : trustedDevices.length === 0 ? (
          <p className="text-virgo-teal-400">
            No trusted devices. When you verify with 2FA, you can choose to trust a device for 90 days.
          </p>
        ) : (
          <div className="space-y-2">
            {trustedDevices.map((device) => (
              <div
                key={device.id}
                className="flex items-center justify-between bg-virgo-navy-600 rounded-lg p-4 border border-virgo-teal-700/30"
              >
                <div className="flex items-center">
                  <Laptop className="text-virgo-teal-400 mr-3 flex-shrink-0" size={20} />
                  <div>
                    <p className="text-white font-medium">{device.device_name || 'Unknown device'}</p>
                    <p className="text-xs text-virgo-teal-500">
                      {device.ip_address || 'Unknown IP'}
                      {' • '}Trusted {device.created_at ? new Date(device.created_at).toLocaleDateString() : 'Unknown'}
                      {' • '}Last used {formatTimeAgo(device.last_used_at)}
                      {' • '}Expires {device.expires_at ? new Date(device.expires_at).toLocaleDateString() : 'Unknown'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => revokeTrustedDevice(device.id)}
                  className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded transition"
                  title="Remove trusted device"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default Security;
